import React, { useCallback, useMemo } from "react";

import { Box, Menu } from "@mui/material";
import { ContextMenuEnabledOptions } from "components/popups";
import "components/popups/Context-menu/ContextMenu.styles.scss";
import {
  ContextMenuOptionFunctionType,
  ContextMenuOptionObjectType,
  ContextMenuOptions,
  ContextMenuOptionType,
  ContextMenuOptionValue,
  IOptionProps,
  OptionsHash
} from "components/popups/Context-menu/Options-collection";
import { CustomComponent } from "store/types";

interface Props {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  options: ContextMenuOptions;
  enabledOptions?: ContextMenuEnabledOptions;
  pasteLineAfter?: ContextMenuOptionType | ContextMenuOptionType[];
}

export const FileActionsPrompt: React.FC<Props> = ({
  anchorEl,
  open,
  onClose,
  options,
  enabledOptions = [],
  pasteLineAfter = []
}) => {
  const optionsArray = useMemo(() => {
    const optionKeys = Object.keys(options) as ContextMenuOptionType[];
    return enabledOptions.length ? optionKeys.filter((option) => enabledOptions.includes(option)) : optionKeys;
  }, [options, enabledOptions]);

  const hasLineAfter = useCallback(
    (option: ContextMenuOptionType): boolean => {
      if (typeof pasteLineAfter === "string") {
        return pasteLineAfter === option;
      }
      return pasteLineAfter.includes(option);
    },
    [pasteLineAfter]
  );

  const handleOptionClick = useCallback(
    async (handler: ContextMenuOptionValue, event?: any) => {
      if (typeof handler === "function") {
        await (handler as ContextMenuOptionFunctionType)(event);
      } else {
        await (handler as ContextMenuOptionObjectType)?.action?.(event);
      }
      onClose();
    },
    [onClose]
  );

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      MenuListProps={{
        sx: {
          p: 0,
          overflow: "visible",
          borderRadius: "8px",

        }
      }}
      PaperProps={{
        sx: {
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
          overflow: "visible"
        }
      }}
    >
      <Box className="context-menu__body" sx={{ borderRadius: "8px" }}>
        {optionsArray.map((key: ContextMenuOptionType, index) => {
          if (!options[key]) return null;
          const Component: CustomComponent<IOptionProps> = OptionsHash[key];
          if (!Component) return null;
          const handler: ContextMenuOptionValue = options[key]!;
          const isPropsFunc: boolean = typeof handler === "function";
          const hasLine: boolean = hasLineAfter(key);
          return (
            <Component
              theme="standard"
              key={`${key}-${index}`}
              hasLineAfter={hasLine}
              title={isPropsFunc ? undefined : (handler as ContextMenuOptionObjectType)?.title}
              onClick={(event?: any) => handleOptionClick(handler, event)}
            />
          );
        })}
      </Box>
    </Menu>
  );
};
